import { useTranslation } from 'react-i18next'
import { Loader2, RefreshCw, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCan } from '@/hooks/useCan'
import { formatDateTime } from '@/lib/dateUtils'
import { estimateRange } from '@/lib/utils/scan-estimate'
import { useRequestScan, useScanEstimate, type VersionScanRequests } from '../scan-requests'

/**
 * Scanning a version again, on its scan report.
 *
 * Andrew, 2026-10-06: "A failed scan needs to be rerun. We need an option for the users to rerun a
 * scan on an individual object version."
 *
 * Three things it has to be honest about:
 *   - a request is QUEUED until Region Management sends it, and says queued, not scanning;
 *   - while a new scan runs, the report beneath is still the previous one, and says so;
 *   - a rerun that failed is shown beside the result that stands, not in place of it.
 *
 * Rules and reasons: backend/src/lib/scan-request.js.
 */
interface Props {
    versionId: number | null | undefined
    scanRequest: VersionScanRequests | undefined
    /** The report's own failure, when the scan it describes did not finish (report.error). */
    scanError?: string | null
    /** The report says the scan failed: scanning again is then the main thing to do. */
    failed?: boolean
    /**
     * The scan report puts "Run scan again" in its header, beside the tabs, where it is on screen
     * on every tab (Andrew, 6 Oct). It then passes its request here, and this panel shows what
     * became of it - queued, running, refused - without a second button.
     */
    request?: ReturnType<typeof useRequestScan>
}

export function ScanRerunPanel({ versionId, scanRequest, scanError, failed = false, request: external }: Props) {
    const { t, i18n } = useTranslation()
    const mayRun = useCan('scan.run')
    const own = useRequestScan()
    const request = external ?? own
    const ownButton = mayRun && !external
    const open = scanRequest?.open || null
    const last = scanRequest?.last || null
    // Asked only when it will be shown: before a person decides to scan, or while one runs.
    const estimate = useScanEstimate([versionId], versionId != null && (mayRun || !!open))
    const range = estimateRange(estimate.data, t)

    if (versionId == null) return null
    const lastFailed = !open && last?.state === 'failed' ? last : null
    if (!open && !ownButton && !scanError && !lastFailed && !request.error) return null

    const when = (d: string | null | undefined) => (d ? formatDateTime(d, i18n.language) : '')
    const failure = request.error as any
    const failureText: string | null = failure
        ? String(t(`scanRequests.error.${failure?.response?.data?.code}`, { defaultValue: failure?.response?.data?.message || t('scanRequests.error.generic') }))
        : null
    const estimateLine = range && (
        <p className="text-xs text-muted-foreground">
            {t('scanRequests.estimate', { range })}
            {estimate.data?.confidence === 'low' && <> {t('scanRequests.estimateLow')}</>}
        </p>
    )

    return (
        <div className="mb-4 space-y-2" data-testid="scan-rerun">
            {/* The scan this report describes did not finish: say why, in the scanner's words. */}
            {scanError && (
                <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800/60 dark:bg-amber-950/30">
                    <TriangleAlert className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                    <p className="text-sm break-words">{t('scanRequests.scanError', { error: scanError })}</p>
                </div>
            )}

            {open ? (
                <div className="flex items-start gap-2.5 rounded-lg border border-dashed border-slate-400 p-3 dark:border-slate-600" role="status">
                    <Loader2 className={`h-5 w-5 flex-shrink-0 mt-0.5 text-slate-600 dark:text-slate-300 ${open.state === 'queued' ? '' : 'animate-spin'}`} />
                    <div className="min-w-0 space-y-0.5">
                        <div className="text-sm font-semibold">{t(open.state === 'queued' ? 'scanRequests.queued' : 'scanRequests.running')}</div>
                        <p className="text-xs">{t('scanRequests.requestedBy', { who: open.requestedByName || t('objects.statusChange.someone'), when: when(open.requestedAt) })}</p>
                        {estimateLine}
                        <p className="text-xs text-muted-foreground">{t('scanRequests.previousShown')}</p>
                    </div>
                </div>
            ) : (
                <>
                    {lastFailed && (
                        <p className="rounded-lg border bg-muted/40 px-3 py-2 text-xs leading-relaxed break-words">
                            {t('scanRequests.lastFailed', { when: when(lastFailed.finishedAt || lastFailed.requestedAt), error: lastFailed.error || t('scanRequests.error.generic') })}
                        </p>
                    )}
                    {ownButton && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <Button
                                variant={failed ? 'default' : 'outline'}
                                size={failed ? 'default' : 'sm'}
                                className="gap-2"
                                disabled={request.isPending}
                                onClick={() => request.mutate(versionId)}
                            >
                                <RefreshCw className={`h-4 w-4 ${request.isPending ? 'animate-spin' : ''}`} />
                                {t('scanRequests.run')}
                            </Button>
                            {estimateLine}
                        </div>
                    )}
                    {failureText && <p role="alert" className="text-xs font-medium text-red-600 dark:text-red-400">{failureText}</p>}
                </>
            )}
        </div>
    )
}
